/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import moment from 'moment'

import {
  FilterSidebar,
  PicklistWithData,
  WorkflowPicklist,
  WorkflowRuns,
  DateRange,
  SvgIcon
} from '../components'


const getInitialState = (queryString) => {
  let defaultState = {
    search: '',
    statusFilter: 'processing',
    workflowFilter: '',
    scheduledAtStartDate: null,
    scheduledAtEndDate: null,
    processedAtStartDate: null,
    processedAtEndDate: null,
    orderBy: 'created_at'
  }

  if (queryString.length > 0) {
    return { ...defaultState, ...getFiltersFromQueryString(queryString) }
  } else {
    defaultState.scheduledAtStartDate = moment().subtract(1, "days");
    return defaultState
  }
}

const getFiltersFromQueryString = (queryString) => {
  let queryParams = qs.parse(queryString)

  let filters = Object.assign(
    {},
    ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
      ['search', 'orderBy'].indexOf(key) > -1
        ? { [key]: queryParams[key] }
        : [ 'scheduledAtStartDate', 'scheduledAtEndDate', 'processedAtStartDate', 'processedAtEndDate' ].indexOf(key) > -1  // end/startDate query params aren't translated into state variables with -'Filter' appended like the others
            ? { [ key ]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
            : { [`${key}Filter`]: queryParams[key] }
    ))
  )

  return filters
}


class WorkflowList extends React.PureComponent {
  state = getInitialState(this.props.location.search)

  componentDidMount() {
    if (this.props.location.search.length === 0) {
      this.setQueryString(true)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      let cleanState = {
        search: '',
        statusFilter: '',
        workflowFilter: '',
        scheduledAtStartDate: null,
        scheduledAtEndDate: null,
        processedAtStartDate: null,
        processedAtEndDate: null,
        orderBy: 'created_at'
      }
      this.setState({ ...cleanState, ...getFiltersFromQueryString(this.props.location.search) })
    }
  }

  setQueryString = (firstRender) => {
    let { orderBy, search, statusFilter, workflowFilter, scheduledAtStartDate, scheduledAtEndDate, processedAtStartDate, processedAtEndDate } = this.state
    let filters = {
      orderBy: orderBy
    }

    if (search !== '') {
      filters.search = search
    }

    if (statusFilter) {
      filters.status = statusFilter
    }

    if (workflowFilter) {
      filters.workflow = workflowFilter
    }

    if (scheduledAtStartDate) {
      filters.scheduledAtStartDate = scheduledAtStartDate.format('YYYYMMDDTHHmm')
    }
    if (scheduledAtEndDate) {
      filters.scheduledAtEndDate = scheduledAtEndDate.format('YYYYMMDDTHHmm')
    }

    if (processedAtStartDate) {
      filters.processedAtStartDate = processedAtStartDate.format('YYYYMMDDTHHmm')
    }
    if (processedAtEndDate) {
      filters.processedAtEndDate = processedAtEndDate.format('YYYYMMDDTHHmm')
    }

    if (firstRender) {
      this.props.history.replace({ search: qs.stringify(filters) })
    } else {
      this.props.history.push({ search: qs.stringify(filters) })
    }
  }

  render() {
    let filters = [
      {
        title: 'Workflow',
        options: <WorkflowPicklist
          id='workflow'
          default='All'
          value={this.state.workflowFilter}
          onChange={workflow => (
            this.setState(
              {
                workflowFilter: workflow === 'All' ? '' : workflow
              },
              this.setQueryString
            )
          )}
        />
      },
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='WorkflowRunStepStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter}
          onChange={status => (
            this.setState(
              {
                statusFilter: status === 'all' ? '' : status
              }, this.setQueryString
            )
          )}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Scheduled between',
        options: <DateRange
          startDate={this.state.scheduledAtStartDate}
          endDate={this.state.scheduledAtEndDate}
          onStartDateChange={date => this.setState({ scheduledAtStartDate: date }, this.setQueryString)}
          onEndDateChange={date => this.setState({ scheduledAtEndDate: date }, this.setQueryString)}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      },
      {
        title: 'Processed between',
        options: <DateRange
          startDate={this.state.processedAtStartDate}
          endDate={this.state.processedAtEndDate}
          onStartDateChange={date => this.setState({ processedAtStartDate: date }, this.setQueryString)}
          onEndDateChange={date => this.setState({ processedAtEndDate: date }, this.setQueryString)}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
          disabled={this.state.statusFilter !== 'processed'}
        />
      }
    ]

    return (
      <div className="slds-grow slds-scrollable_y" role="main">
        <div className="slds-grid slds-wrap slds-size_1-of-1 slds-clearfix">
          <div className="tab-nav slds-size_1-of-1 u-bg-color-white">
            <div className="u-font-size-x-large u-text-caps slds-p-left_large slds-m-bottom_small slds-grid">
              <SvgIcon
                variant="utility"
                symbol="list"
                colour="light"
                className="slds-m-right_medium slds-m-top_x-small"
              />
              <div className="slds-m-top_small">Workflow runs</div>
            </div>
          </div>
          <FilterSidebar
            filters={filters}
            filtersApplied={
              [
                this.state.statusFilter,
                this.state.workflowFilter,
                this.state.scheduledAtStartDate
                  ? this.state.scheduledAtStartDate.toString()
                  : "",
                this.state.scheduledAtEndDate
                  ? this.state.scheduledAtEndDate.toString()
                  : "",
                this.state.processedAtStartDate
                  ? this.state.processedAtStartDate.toString()
                  : "",
                this.state.processedAtEndDate
                  ? this.state.processedAtEndDate.toString()
                  : "",
              ].filter((filter) => filter.length > 0).length
            }
            onClear={() =>
              this.setState(
                {
                  statusFilter: "",
                  workflowFilter: "",
                  scheduledAtStartDate: null,
                  scheduledAtEndDate: null,
                  processedAtStartDate: null,
                  processedAtEndDate: null,
                },
                this.setQueryString
              )
            }
            search={true}
            searchString={this.state.search}
            onSearchClick={(search) => this.setState({ search })}
            onSearchClear={() => this.setState({ search: "" })}
          />
          <div className="slds-col slds-size_10-of-12">
            <WorkflowRuns
              search={this.state.search || null}
              status={this.state.statusFilter}
              workflow={this.state.workflowFilter}
              scheduledAtStartDate={
                this.state.scheduledAtStartDate
                  ? moment
                      .utc(this.state.scheduledAtStartDate)
                      .format("YYYY-MM-DD HH:mm")
                  : null
              }
              scheduledAtEndDate={
                this.state.scheduledAtEndDate
                  ? moment
                      .utc(this.state.scheduledAtEndDate)
                      .format("YYYY-MM-DD HH:mm")
                  : null
              }
              processedAtStartDate={
                this.state.processedAtStartDate
                  ? moment
                      .utc(this.state.processedAtStartDate)
                      .format("YYYY-MM-DD HH:mm")
                  : null
              }
              processedAtEndDate={
                this.state.processedAtEndDate
                  ? moment
                      .utc(this.state.processedAtEndDate)
                      .format("YYYY-MM-DD HH:mm")
                  : null
              }
              orderBy={this.state.orderBy}
              changeSort={(orderBy) =>
                this.setState({ orderBy }, this.setQueryString)
              }
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(WorkflowList)



// WEBPACK FOOTER //
// ./src/containers/WorkflowList.js